/* jshint ignore:start */

const THREE = window.THREE;

const canvas = document.querySelector(".murmuration");
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const splash = document.querySelector(".splash");
const splashLink = document.querySelector(".splash__link");

if (splash && splashLink) {
  // Lock page scrolling while the full-screen introduction is covering the site.
  document.documentElement.classList.add("splash-active");

  splashLink.addEventListener("click", () => {
    if (splash.classList.contains("splash--dismissed")) return;

    // Remember the dismissal for one day so PHP can omit the splash markup,
    // Three.js and this animation file on future page loads.
    const secureCookie = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `ef_splash_hidden=1; Max-Age=86400; Path=/; SameSite=Lax${secureCookie}`;

    // CSS handles the fade so the overlay remains visible throughout the transition.
    splash.classList.add("splash--dismissed");
    const finishDismissal = event => {
      if (event.propertyName !== "opacity") return;

      // Restore the page only after the opacity transition has completely finished.
      splash.removeEventListener("transitionend", finishDismissal);
      splash.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("splash-active");
      document.body.classList.add("splash-overlay-dismissed");

      // Let the particle renderer release its WebGL resources once it is hidden.
      splash.dispatchEvent(new Event("splash:dismissed"));
    };
    splash.addEventListener("transitionend", finishDismissal);
  });
}

if (!reduceMotion.matches && canvas && THREE) {
  startMurmuration(canvas).catch(() => {
    document.documentElement.classList.add("webgl-fallback");
  });
}

function startMurmuration(surface) {
  return Promise.resolve().then(() => {
    const compact = window.matchMedia("(max-width: 700px)").matches;
    const stackedLogoQuery = window.matchMedia("(max-width: 999px)");
    const stackedLogo = stackedLogoQuery.matches;
    const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
    // Keep the responsive canvas sharp enough for the stacked wordmark while
    // retaining a lower pixel cost than the desktop field.
    const pixelRatioCap = 1;
    const pixelRatio = Math.min(window.devicePixelRatio, pixelRatioCap);
    const renderer = new THREE.WebGLRenderer({
      canvas: surface,
      alpha: true,
      antialias: false,
      powerPreference: "high-performance"
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(pixelRatio);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 100);
    camera.position.z = 10;

    // Use a deliberately light point field so the splash remains responsive on
    // Safari while retaining enough samples for the wordmark to stay readable.
    const count = stackedLogo ? (compact ? 3000 : 4000) : coarsePointer ? 6000 : 8000;
    const position = new Float32Array(count * 3);
    const velocity = new Float32Array(count * 2);
    const targets = new Float32Array(count * 2);
    const idleTargets = new Float32Array(count * 2);
    const random = new Float32Array(count * 4);
    const flow = new Float32Array(count * 4);
    const colorPhase = new Float32Array(count);
    const formedColors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const halo = new Float32Array(count);
    const lockTiming = new Float32Array(count * 2);
    const release = new Float32Array(count);
    const disruptionOffset = new Float32Array(count * 2);
    const flowHover = new Float32Array(count);
    // Three.js converts these linear values to sRGB at output, producing the
    // same visible colours as the theme's CSS utilities.
    const formedCoral = new THREE.Color("#ff7448");
    const formedPurple = new THREE.Color("#6007d5");
    const flowColumns = Math.round(Math.sqrt(count * 2.55));
    const flowRows = Math.ceil(count / flowColumns);

    for (let i = 0; i < count; i += 1) {
      const i3 = i * 3;
      const i2 = i * 2;
      const i4 = i * 4;
      random[i4] = Math.random();
      random[i4 + 1] = Math.random();
      random[i4 + 2] = Math.random() * Math.PI * 2;
      const flowColumn = i % flowColumns;
      const flowRow = Math.floor(i / flowColumns);
      const flowU = (flowColumn / Math.max(1, flowColumns - 1)) * 2 - 1;
      const flowV = (flowRow / Math.max(1, flowRows - 1)) * 2 - 1;
      const flowGroupPosition = (flowU + 1) * 2;
      const flowGroup = Math.min(3, Math.floor(flowGroupPosition));
      flow[i4] = flowU;
      flow[i4 + 1] = flowV;
      flow[i4 + 2] = (flowGroupPosition - flowGroup - 0.5) * 2;
      flow[i4 + 3] = flowGroup * 1.37 + random[i4 + 1] * 0.35;
      position[i3 + 2] = (Math.random() - 0.5) * 8;
      velocity[i2] = (Math.random() - 0.5) * 3;
      velocity[i2 + 1] = (Math.random() - 0.5) * 3;
      halo[i] = random[i4 + 1] > (stackedLogo ? 0.995 : 0.97) ? 1 : 0;

      colorPhase[i] = random[i4 + 2];
      // A centred logo would otherwise sit entirely inside the coral part of
      // the viewport gradient. Keep a visible purple minority when formed.
      const formedIsCoral = random[i4 + 1] < 0.92;
      const formedColor = formedIsCoral ? formedCoral : formedPurple;
      formedColors[i3] = formedColor.r;
      formedColors[i3 + 1] = formedColor.g;
      formedColors[i3 + 2] = formedColor.b;
      // Supply valid initial values; resize() replaces these with timings based
      // on spatial logo pieces once the sampled mark dimensions are known.
      lockTiming[i2] = 0.34 + Math.sqrt(random[i4]) * 0.28;
      lockTiming[i2 + 1] = lockTiming[i2] + 0.12 + random[i4 + 1] * 0.04;
      sizes[i] = 1.9 + Math.random() * 1.1;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(position, 3));
    geometry.setAttribute("aColorPhase", new THREE.BufferAttribute(colorPhase, 1));
    geometry.setAttribute("aFormedColor", new THREE.BufferAttribute(formedColors, 3));
    geometry.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute("aHalo", new THREE.BufferAttribute(halo, 1));
    geometry.setAttribute("aLockTiming", new THREE.BufferAttribute(lockTiming, 2));
    geometry.setAttribute("aRelease", new THREE.BufferAttribute(release, 1));

    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      // Normal blending prevents overlapping gradient points accumulating to white.
      uniforms: {
        uPixelRatio: { value: pixelRatio },
        uOpacity: { value: 0 },
        uFormation: { value: 0 },
        uTime: { value: 0 },
        // Responsive marks use fewer particles, so retain more of each dot's
        // size after lock-in to close gaps without adding simulation work.
        uFormedSizeReduction: { value: stackedLogo ? 0.16 : 0.22 },
        uViewportHalfHeight: { value: 1 }
      },
      vertexShader: `
      attribute float aColorPhase;
      attribute vec3 aFormedColor;
      attribute float aSize;
      attribute float aHalo;
      attribute vec2 aLockTiming;
      attribute float aRelease;
      varying vec3 vGradientColor;
      varying vec3 vFormedColor;
      varying float vDepthAlpha;
      varying float vFormation;
      varying float vRelease;
      varying float vHalo;
      uniform float uPixelRatio;
      uniform float uOpacity;
      uniform float uFormation;
      uniform float uTime;
      uniform float uFormedSizeReduction;
      uniform float uViewportHalfHeight;

      void main() {
        float distanceFromMiddle = clamp(abs(position.y) / uViewportHalfHeight, 0.0, 1.0);
        float edgeProgress = clamp((distanceFromMiddle - 0.28) / 0.58, 0.0, 1.0);
        float edgeBlend = edgeProgress * edgeProgress * (3.0 - 2.0 * edgeProgress);
        float colorVariation = sin(aColorPhase + uTime * 0.28) * 0.045;
        float colorMix = clamp(0.94 - edgeBlend * 0.89 + colorVariation, 0.03, 0.97);
        vec3 purple = vec3(0.1170, 0.0021, 0.6654);
        vec3 coral = vec3(1.0, 0.1746, 0.0648);
        vGradientColor = mix(purple, coral, colorMix);
        vFormedColor = aFormedColor;
        float rawFormation = clamp((uFormation - aLockTiming.x) / (aLockTiming.y - aLockTiming.x), 0.0, 1.0);
        vFormation = rawFormation * rawFormation * rawFormation *
          (rawFormation * (rawFormation * 6.0 - 15.0) + 10.0);
        vRelease = aRelease;
        vHalo = aHalo;
        float depthScale = clamp(1.0 + position.z * 0.025, 0.84, 1.16);
        vDepthAlpha = clamp(0.82 + position.z * 0.025, 0.62, 1.0);
        float effectiveFormation = vFormation * (1.0 - aRelease);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        // Preserve the fuller moving field, then reduce each dot as it locks so
        // counters and letter edges resolve crisply at every breakpoint.
        float formedSizeReduction = uFormedSizeReduction + aHalo * 0.22;
        gl_PointSize = aSize * uPixelRatio * depthScale * (1.0 - effectiveFormation * formedSizeReduction);
      }
    `,
      fragmentShader: `
      varying vec3 vGradientColor;
      varying vec3 vFormedColor;
      varying float vDepthAlpha;
      varying float vFormation;
      varying float vRelease;
      varying float vHalo;
      uniform float uOpacity;

      void main() {
        float effectiveFormation = vFormation * (1.0 - vRelease);
        vec2 point = gl_PointCoord - 0.5;
        float dist = length(point);
        float softCircle = 1.0 - smoothstep(0.34, 0.5, dist);
        float crispCircle = 1.0 - smoothstep(0.45, 0.5, dist);
        float circle = mix(softCircle, crispCircle, effectiveFormation);
        float core = 1.0 - smoothstep(0.0, 0.33, dist);
        vec3 resolvedColor = mix(vFormedColor, vGradientColor, vHalo);
        vec3 baseColor = mix(vGradientColor, resolvedColor, effectiveFormation);
        float movementHighlight = 1.0 - smoothstep(0.65, 0.9, effectiveFormation);
        vec3 particleColor = baseColor + core * 0.08 * movementHighlight;
        float particleAlpha = mix(vDepthAlpha, 1.0, effectiveFormation);
        gl_FragColor = vec4(particleColor, circle * uOpacity * particleAlpha);
        #include <colorspace_fragment>
      }
    `
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    const pointer = {
      x: 0,
      y: 0,
      active: false,
      energy: 0,
      vx: 0,
      vy: 0,
      clientX: null,
      clientY: null
    };
    let width = 1;
    let height = 1;
    let resolvedLogoWidth = 1;
    let resolvedLogoHeight = 1;
    let animationFrame = 0;
    let lastPhysicsTime = 0;
    let lastTime = performance.now();
    let startTime = lastTime;
    let lastInteraction = lastTime;
    let formationProgress = 0;
    let formationStarted = false;
    let formationStartedAt = null;
    let formationLocked = false;
    // Leave a longer opening interaction window, then reveal logo fragments
    // progressively once the user has had time to disturb the moving field.
    const initialFormationDelay = 3200;
    const formationDuration = 2800;
    const formedLogoReturnDelay = 100;
    let scatterEnergy = 0;
    let returnBlend = 0;
    let resolvedMotionBlend = 0;
    let visible = !document.hidden;
    const targetFrameInterval = 1000 / (stackedLogo ? 30 : 60);
    // Allow for small requestAnimationFrame timestamp differences near the
    // target boundary without accidentally skipping an additional frame.
    const frameIntervalTolerance = 1;
    const desktopLogoSource = surface.dataset.logoSrc;
    const compactLogoSource = surface.dataset.logoSrcCompact || desktopLogoSource;
    const initialLogoSource = stackedLogo ? compactLogoSource : desktopLogoSource;
    const initialLogoAspectRatio = stackedLogo ? 275 / 133 : 14.08;

    return sampleLogo(initialLogoSource, count, initialLogoAspectRatio).then(initialLogoPoints => {
      let desktopLogoPoints = stackedLogo ? null : initialLogoPoints;
      let compactLogoPoints = stackedLogo ? initialLogoPoints : null;

      function resize() {
        width = surface.clientWidth;
        height = surface.clientHeight;
        renderer.setSize(width, height, false);
        camera.left = -width / 2;
        camera.right = width / 2;
        camera.top = height / 2;
        camera.bottom = -height / 2;
        camera.updateProjectionMatrix();
        const useStackedLogo = stackedLogoQuery.matches;
        const logoPoints = (useStackedLogo ? compactLogoPoints : desktopLogoPoints) || initialLogoPoints;
        const logoWidth = useStackedLogo
          ? compact
            ? Math.min(width * 0.84, 500)
            : Math.min(width * 0.72, 560)
          : Math.min(width * 0.9, 1300);
        const logoHeight = useStackedLogo ? logoWidth / (275 / 133) : logoWidth / 14.08;
        resolvedLogoWidth = logoWidth;
        resolvedLogoHeight = logoHeight;
        material.uniforms.uViewportHalfHeight.value = Math.max(1, height * 0.5);
        const verticalOffset = 0;

        for (let i = 0; i < count; i += 1) {
          const i2 = i * 2;
          const i4 = i * 4;
          const sampledX = logoPoints[i2];
          const sampledY = logoPoints[i2 + 1];
          const idleColumn = (flow[i4] + 1) * 0.5;
          // Most particles form a crisp core; a smaller halo stays loose enough to
          // keep the mark feeling alive rather than turning into a raster image.
          const isCoreParticle = random[i4] < (useStackedLogo ? 0.9998 : 0.995);
          const edgeLooseness = isCoreParticle
            ? useStackedLogo
              ? 0.002 + random[i4 + 1] * 0.01
              : 0.008 + random[i4 + 1] * 0.045
            : useStackedLogo
            ? 0.02 + random[i4 + 1] * 0.04
            : 0.08 + random[i4 + 1] * 0.22;
          targets[i2] = (sampledX - 0.5) * logoWidth + Math.cos(random[i4 + 2] * 2.7) * edgeLooseness;
          targets[i2 + 1] =
            (0.5 - sampledY) * logoHeight + verticalOffset + Math.sin(random[i4 + 2] * 1.9) * edgeLooseness * 0.55;
          // Resolve neighbouring dots as small spatial pieces in a shuffled
          // order. More, smaller pieces make recognisable fragments appear
          // early without exposing a rigid grid across the finished mark.
          const pieceColumns = useStackedLogo ? 5 : 12;
          const pieceRows = useStackedLogo ? 7 : 3;
          const pieceX = Math.min(pieceColumns - 1, Math.floor(sampledX * pieceColumns));
          const pieceY = Math.min(pieceRows - 1, Math.floor(sampledY * pieceRows));
          const pieceOrderSeed = Math.sin((pieceX + 1) * 12.9898 + (pieceY + 1) * 78.233) * 43758.5453;
          const pieceOrder = pieceOrderSeed - Math.floor(pieceOrderSeed);
          const pieceJitter = (random[i4] - 0.5) * 0.07;
          lockTiming[i2] = Math.max(0.02, Math.min(0.78, 0.035 + pieceOrder * 0.72 + pieceJitter));
          lockTiming[i2 + 1] = Math.min(0.98, lockTiming[i2] + 0.1 + random[i4 + 1] * 0.08);
          idleTargets[i2] = flow[i4] * width * 0.54;
          idleTargets[i2 + 1] = flow[i4 + 1] * height * 0.46 + Math.sin(idleColumn * Math.PI * 4) * height * 0.06;

          if (position[i * 3] === 0 && position[i * 3 + 1] === 0) {
            seedParticle(i);
          }
        }
        geometry.attributes.aLockTiming.needsUpdate = true;
      }

      function seedParticle(i) {
        const i3 = i * 3;
        const i4 = i * 4;
        const i2 = i * 2;
        const angle = random[i4 + 2];
        position[i3] = idleTargets[i2] + Math.cos(angle) * width * 0.035;
        position[i3 + 1] = idleTargets[i2 + 1] + Math.sin(angle * 1.7) * height * 0.08;
      }

      function registerInteraction() {
        lastInteraction = performance.now();
        returnBlend = 0;
      }

      function updatePointer(event) {
        const source = event.touches?.[0] ?? event;
        const bounds = surface.getBoundingClientRect();
        const isFirstPointerPosition = pointer.clientX === null;
        const movement = isFirstPointerPosition
          ? Infinity
          : Math.hypot(source.clientX - pointer.clientX, source.clientY - pointer.clientY);
        if (pointer.clientX !== null) {
          pointer.vx = pointer.vx * 0.35 + (source.clientX - pointer.clientX) * 0.65;
          pointer.vy = pointer.vy * 0.35 - (source.clientY - pointer.clientY) * 0.65;
        }
        pointer.x = source.clientX - bounds.left - width / 2;
        pointer.y = height / 2 - (source.clientY - bounds.top);
        pointer.active = true;
        pointer.clientX = source.clientX;
        pointer.clientY = source.clientY;
        if (movement > 0.25) {
          pointer.energy = 1;
        }
        // Match the development-branch logo interaction: formed particles
        // recover independently instead of pointer movement postponing return.
        if (movement > 1.5 && !formationLocked) {
          registerInteraction();
        }
      }

      function clearPointer() {
        pointer.active = false;
        pointer.clientX = null;
        pointer.clientY = null;
      }

      function burst(amount = 1) {
        scatterEnergy = Math.max(scatterEnergy, amount);
        for (let i = 0; i < count; i += 1) {
          const i2 = i * 2;
          const i3 = i * 3;
          const angle =
            Math.atan2(position[i3 + 1] - pointer.y, position[i3] - pointer.x) + (Math.random() - 0.5) * 1.8;
          const impulse = 3 + Math.random() * 8;
          velocity[i2] += Math.cos(angle) * impulse;
          velocity[i2 + 1] += Math.sin(angle) * impulse;
        }
      }

      function replay() {
        startTime = performance.now();
        registerInteraction();
        formationProgress = 0;
        formationStarted = false;
        formationStartedAt = null;
        formationLocked = false;
        release.fill(0);
        disruptionOffset.fill(0);
        flowHover.fill(0);
        scatterEnergy = 0.5;
        returnBlend = 0;
        resolvedMotionBlend = 0;
        for (let i = 0; i < count; i += 1) {
          seedParticle(i);
        }
      }

      function animate(now) {
        animationFrame = requestAnimationFrame(animate);
        if (!visible) {
          return;
        }
        if (now - lastPhysicsTime < targetFrameInterval - frameIntervalTolerance) {
          return;
        }
        lastPhysicsTime = now;
        const dt = Math.min((now - lastTime) / 16.667, 2.2);
        const elapsed = (now - startTime) / 1000;
        lastTime = now;

        // Begin revealing the first logo fragments shortly after the opening
        // field settles. Initial cursor movement does not postpone the resolve.
        const inactivityDuration = now - lastInteraction;
        // Return locally disrupted logo particles on their separate, shorter
        // delay once the mark has already completed its first resolve.
        const initialDelayElapsed = now - startTime >= initialFormationDelay;
        const interactionIsActive = pointer.active && inactivityDuration < 240;
        const cursorIsStill = formationLocked ? inactivityDuration >= formedLogoReturnDelay : initialDelayElapsed;
        const returnBlendTarget = cursorIsStill ? 1 : 0;
        returnBlend += (returnBlendTarget - returnBlend) * (1 - Math.pow(0.88, dt));
        if (cursorIsStill && !formationStarted) {
          formationStarted = true;
          formationStartedAt = now;
        }
        // A fixed duration prevents an exponential tail from extending the
        // resolve after the fixed opening delay.
        if (formationStarted) {
          formationProgress = Math.min(1, (now - formationStartedAt) / formationDuration);
        }
        if (formationStarted && formationProgress > 0.985) {
          formationLocked = true;
        }
        // Keep the resolved wordmark still and tightly defined on every device.
        // Pointer interaction can still release individual particles.
        const resolvedMotionTarget = 0;
        resolvedMotionBlend += (resolvedMotionTarget - resolvedMotionBlend) * (1 - Math.pow(0.94, dt));
        const formation = formationProgress * (1 - scatterEnergy * 0.52);
        const drift = 1 - formation;
        const logoSettled = formationProgress >= 1 && formation >= 0.999 && scatterEnergy < 0.002;
        const responsiveSettledMotion = stackedLogo ? drift : 1;
        const logoBreath = 1 + Math.sin(elapsed * 0.53) * 0.0015 * responsiveSettledMotion;
        const motionTime = elapsed * 1.6;
        const flowScaleX = width * 0.98;
        const flowScaleY = height * 0.86;
        const verticalJourney = Math.sin(motionTime * 0.34);
        const verticalDrift = Math.sin(motionTime * 0.17 + 1.2) * 0.07;
        // Keep only a subtle shared journey; the individual clouds provide most
        // of the vertical travel at their own speeds below.
        const currentCenterX =
          width * (Math.sin(motionTime * 0.11 + 0.7) * 0.075 + Math.sin(motionTime * 0.27) * 0.025);
        const currentCenterY = height * (0.02 - verticalJourney * 0.08 - verticalDrift * 0.7);
        material.uniforms.uTime.value = elapsed;
        scatterEnergy *= Math.pow(0.975, dt);
        // Formation follows its fixed schedule, but live pointer movement keeps
        // full interaction energy throughout the transition into the logo.
        if (interactionIsActive && pointer.energy > 0) {
          pointer.energy = 1;
        } else {
          pointer.energy *= Math.pow(0.9, dt);
          if (pointer.energy < 0.02) {
            pointer.energy = 0;
          }
        }
        pointer.vx *= Math.pow(0.86, dt);
        pointer.vy *= Math.pow(0.86, dt);
        const movingOpacity = 0.82;
        const opacityTarget = movingOpacity + (1 - movingOpacity) * formation;
        material.uniforms.uOpacity.value +=
          (Math.min(elapsed * 1.4, opacityTarget) - material.uniforms.uOpacity.value) * 0.05;
        material.uniforms.uFormation.value += (formation - material.uniforms.uFormation.value) * 0.08;

        // Periodically loosen a small, randomly selected part of the resolved
        // mark. The centre always lands on a sampled logo point, while the
        // sine envelope makes both the break and recovery continuous.
        const fractureCycleDuration = 3.8;
        const fractureCycle = Math.floor(elapsed / fractureCycleDuration);
        const fractureProgress = (elapsed % fractureCycleDuration) / fractureCycleDuration;
        const fractureSeedRaw = Math.sin((fractureCycle + 1) * 91.345) * 47453.5453;
        const fractureSeed = fractureSeedRaw - Math.floor(fractureSeedRaw);
        const fractureParticle = Math.min(count - 1, Math.floor(fractureSeed * count));
        const fractureCenterX = targets[fractureParticle * 2];
        const fractureCenterY = targets[fractureParticle * 2 + 1];
        const fractureRadiusX = Math.max(38, resolvedLogoWidth * 0.055);
        const fractureRadiusY = Math.max(28, resolvedLogoHeight * 0.34);
        const fractureRadiusXSquared = fractureRadiusX * fractureRadiusX;
        const fractureRadiusYSquared = fractureRadiusY * fractureRadiusY;
        const fractureEnabled = Math.cos((fractureCycle + 1) * 17.17) > -0.45 ? 1 : 0;
        const fracturePulse = Math.sin(fractureProgress * Math.PI) ** 2 * fractureEnabled * resolvedMotionBlend;
        const minimumViewportDimension = Math.min(width, height);
        const releaseWidth = formationLocked ? 105 : Math.max(95, resolvedLogoWidth * 0.09);
        const releaseHeight = formationLocked ? 62 : Math.max(60, resolvedLogoHeight * 0.18);
        const flowHoverWidth = releaseWidth * 1.2;
        const flowHoverHeight = releaseHeight * 0.7;
        const pointerIsInteractive = pointer.active && pointer.energy > 0;
        const cursorSpeed = Math.min(24, Math.hypot(pointer.vx, pointer.vy));
        const pointerSweepX = Math.max(-24, Math.min(24, pointer.vx)) * 2.6;
        const pointerSweepY = Math.max(-24, Math.min(24, pointer.vy)) * 2.6;
        const restingHoverDamping = Math.pow(0.95, dt);
        const movingHoverDamping = Math.pow(0.992, dt);

        for (let i = 0; i < count; i += 1) {
          const i2 = i * 2;
          const i3 = i * 3;
          const i4 = i * 4;
          const x = position[i3];
          const y = position[i3 + 1];
          const phase = random[i4 + 2];
          let releaseDistance = Infinity;
          if (formationLocked && pointerIsInteractive) {
            const releaseX = targets[i2] - pointer.x + Math.sin(phase * 2.3) * releaseWidth * 0.16;
            const releaseY = targets[i2 + 1] - pointer.y + Math.cos(phase * 1.7) * releaseHeight * 0.14;
            releaseDistance = Math.hypot(releaseX / releaseWidth, releaseY / releaseHeight);
          }

          // Most particles in the completed responsive logo are stationary and
          // do not need the opening cloud, vortex, roaming, or ripple maths. A
          // particle under the pointer or returning from a release
          // keeps using the complete simulation below.
          if (logoSettled && release[i] <= 0.002 && releaseDistance >= 1) {
            release[i] = 0;
            flowHover[i] *= restingHoverDamping;
            const targetX = targets[i2];
            const targetY = targets[i2 + 1];
            velocity[i2] += (targetX - x) * 0.0155 * dt;
            velocity[i2 + 1] += (targetY - y) * 0.0155 * dt;
            velocity[i2] += Math.cos(y * 0.008 + elapsed * 0.72 + phase) * 0.012 * dt;
            velocity[i2 + 1] += Math.sin(x * 0.007 - elapsed * 0.61 + phase) * 0.01 * dt;

            const speed = Math.hypot(velocity[i2], velocity[i2 + 1]);
            if (speed > 4) {
              velocity[i2] = (velocity[i2] / speed) * 4;
              velocity[i2 + 1] = (velocity[i2 + 1] / speed) * 4;
            }

            const damping = Math.pow(0.93, dt);
            velocity[i2] *= damping;
            velocity[i2 + 1] *= damping;
            position[i3] += velocity[i2] * dt;
            position[i3 + 1] += velocity[i2 + 1] * dt;
            position[i3] += (targetX - position[i3]) * 0.065 * dt;
            position[i3 + 1] += (targetY - position[i3 + 1]) * 0.065 * dt;
            position[i3 + 2] += -position[i3 + 2] * 0.055 * dt;
            continue;
          }

          const u = flow[i4];
          const v = flow[i4 + 1];
          const groupU = flow[i4 + 2];
          const groupPhase = flow[i4 + 3];
          const currentCluster = Math.min(3, Math.floor((u + 1) * 2));
          const settledWander = random[i4] < 0.98 ? 0.04 : 0.22;
          const logoRipple = Math.sin(targets[i2] * 0.018 - elapsed * 1.15 + phase * 0.12);
          let resolvedWanderX = 0;
          let resolvedWanderY = 0;
          let fractureOffsetX = 0;
          let fractureOffsetY = 0;
          let haloOffsetX = 0;
          let haloOffsetY = 0;

          // Responsive logos intentionally have no settled ambient motion. Skip
          // its trigonometry entirely there and on desktop before resolution.
          if (resolvedMotionBlend > 0) {
            const resolvedWanderRange = 1.05 + random[i4 + 1] * 1.75;
            const resolvedWanderAngle = elapsed * (0.38 + random[i4] * 0.65) + phase;
            resolvedWanderX =
              (Math.sin(resolvedWanderAngle) + Math.sin(resolvedWanderAngle * 0.43 + phase * 1.7) * 0.35) *
              resolvedWanderRange *
              resolvedMotionBlend;
            resolvedWanderY =
              (Math.cos(resolvedWanderAngle * 0.79 + phase * 0.31) +
                Math.sin(resolvedWanderAngle * 0.37 + phase) * 0.3) *
              resolvedWanderRange *
              0.85 *
              resolvedMotionBlend;

            const fractureX = targets[i2] - fractureCenterX;
            const fractureY = targets[i2 + 1] - fractureCenterY;
            const fractureDistance =
              (fractureX * fractureX) / fractureRadiusXSquared + (fractureY * fractureY) / fractureRadiusYSquared;
            const fractureEdge = Math.max(0, 1 - fractureDistance);
            const fractureParticipation = random[i4] > 0.24 ? 1 : 0.12;
            const fractureStrength =
              fractureEdge * fractureEdge * fracturePulse * fractureParticipation * (1 - release[i]);
            const fractureAngle = phase * 2.31 + fractureCycle * 1.73;
            const fractureRange = 6 + random[i4 + 1] * 13;
            fractureOffsetX = Math.cos(fractureAngle) * fractureRange * fractureStrength;
            fractureOffsetY = Math.sin(fractureAngle) * fractureRange * fractureStrength * 0.72;

            // A small subset eases out from its letter position after resolve,
            // then follows a slow, irregular orbit close to the mark.
            const haloStrength = halo[i] * resolvedMotionBlend * (1 - release[i]);
            const haloBaseAngle = phase * 1.37;
            const haloBaseRange = 14 + random[i4] * 26;
            const haloOrbitAngle = elapsed * (0.14 + random[i4 + 1] * 0.2) * (random[i4] > 0.5 ? 1 : -1) + phase * 0.73;
            const haloOrbitRange = 6 + random[i4 + 1] * 9;
            haloOffsetX =
              (Math.cos(haloBaseAngle) * haloBaseRange + Math.cos(haloOrbitAngle) * haloOrbitRange) * haloStrength;
            haloOffsetY =
              (Math.sin(haloBaseAngle) * haloBaseRange * 0.72 + Math.sin(haloOrbitAngle * 0.81) * haloOrbitRange) *
              haloStrength;
          }
          const logoX =
            targets[i2] * logoBreath +
            logoRipple * (random[i4] < 0.98 ? 0.08 : 0.24) * responsiveSettledMotion +
            Math.sin(elapsed * 0.8 + phase) * (0.5 * settledWander * responsiveSettledMotion + drift * 16) +
            resolvedWanderX +
            fractureOffsetX +
            haloOffsetX;
          const logoY =
            targets[i2 + 1] * logoBreath +
            logoRipple * (random[i4] < 0.98 ? 0.18 : 0.42) * responsiveSettledMotion +
            Math.cos(elapsed * 0.65 + phase * 1.3) * (0.34 * settledWander * responsiveSettledMotion + drift * 9) +
            resolvedWanderY +
            fractureOffsetY +
            haloOffsetY;
          // Reference-inspired particle current: four coordinated masses share a
          // traveling sheet, but pulse and fold at slightly offset phases.
          const envelope = 0.48 + 0.52 * Math.pow(Math.max(0, Math.cos(groupU * Math.PI * 0.5)), 0.45);
          const lag = (random[i4 + 1] > 0.72 ? random[i4] * 0.85 : 0) + Math.floor((u + 1) * 2) * 0.055;
          const groupPulse = Math.sin(motionTime * 0.43 + groupPhase);
          const curl = Math.sin(u * 4.15 - (motionTime - lag) * 1.02 + Math.sin((motionTime - lag) * 0.21) * 1.8);
          const cross = Math.sin(v * 2.6 + (motionTime - lag) * 0.66 + u * 2.2 + groupPhase * 0.18);
          const fine = Math.sin(u * 9.2 + v * 3.1 - motionTime * 0.48 + phase) * 0.025;
          const pinch = 0.62 + 0.3 * Math.sin(u * 3.3 + motionTime * 0.71) ** 2;
          const groupCenterWeight = 1 - Math.min(1, Math.abs(groupU));
          const clusterBreadth = 0.55 + groupCenterWeight * 0.2 + Math.sin(motionTime * 0.31 + groupPhase) * 0.08;
          // Break the opening sheet into soft, overlapping clouds. Each section
          // has its own width and gently wandering centre, while groupU keeps
          // the edges irregular instead of describing a row of circles.
          const clusterPair = Math.floor(currentCluster / 2);
          const clashDirection = currentCluster % 2 === 0 ? 1 : -1;
          const clashProgress = 0.5 + Math.sin(motionTime * 0.22 + clusterPair * 1.4) * 0.5;
          const clusterTravelSpeed = 0.095 + currentCluster * 0.017;
          const clusterOrbitX = Math.sin(motionTime * clusterTravelSpeed + currentCluster * 1.11) * 0.12;
          const clusterOrbitY = Math.cos(motionTime * clusterTravelSpeed * 0.83 + currentCluster * 0.74) * 0.18;
          // Layer unrelated frequencies for each desktop cluster so its centre
          // wanders organically rather than following the same wave path.
          const desktopClusterWanderX =
            Math.sin(motionTime * (0.07 + currentCluster * 0.013) + currentCluster * 2.11) * 0.2 +
            Math.cos(motionTime * (0.19 + currentCluster * 0.017) + currentCluster * 0.73) * 0.1;
          const desktopClusterWanderY =
            Math.cos(motionTime * (0.09 + currentCluster * 0.011) + currentCluster * 1.67) * 0.24 +
            Math.sin(motionTime * (0.23 - currentCluster * 0.014) + currentCluster * 0.91) * 0.12;
          const clusterCenterX =
            (currentCluster - 1.5) * 0.62 +
            clashDirection * clashProgress * 0.16 +
            Math.sin(motionTime * 0.17 + currentCluster * 1.83) * 0.11 +
            clusterOrbitX +
            desktopClusterWanderX;
          const clusterWidth = 0.27 + Math.sin(motionTime * 0.23 + currentCluster * 1.31) * 0.05;
          const clusterSpeed = 0.13 + currentCluster * 0.028;
          const clusterVerticalOffset =
            (Math.sin(motionTime * clusterSpeed + currentCluster * 1.47) * 0.62 +
              Math.sin(motionTime * (0.29 - currentCluster * 0.018) + currentCluster * 0.83) * 0.11 +
              clusterOrbitY +
              desktopClusterWanderY) *
            0.78;
          let currentX = clusterCenterX + groupU * clusterWidth + groupPulse * 0.045 * groupCenterWeight;
          let currentY =
            v * clusterBreadth * pinch +
            curl * 0.28 * envelope +
            cross * 0.09 +
            fine +
            groupPulse * (0.2 + 0.07 * Math.cos(groupU * Math.PI)) +
            Math.sin(motionTime * 0.24 + groupPhase * 0.63) * 0.1 +
            clusterVerticalOffset;
          let currentZ =
            Math.cos(u * 3.7 - motionTime * 0.94) * 0.42 * envelope +
            Math.sin(v * 3.4 + u * 1.8 + motionTime * 0.54) * 0.18 +
            Math.cos(motionTime * 0.39 + groupPhase) * 0.22 * groupCenterWeight;

          // Slow group turns and small cross-currents keep the sheet graceful and
          // prevent the motion from reading as a single mechanical sine wave.
          currentX +=
            Math.sin(motionTime * 0.21 + groupPhase) * 0.07 + Math.sin(v * 5.2 + motionTime * 0.31 + phase) * 0.025;
          currentY +=
            Math.sin(u * 6.1 - motionTime * 0.57 + phase) * 0.04 +
            Math.cos(groupU * Math.PI + motionTime * 0.28) * 0.055;
          currentZ += Math.sin(u * 2.4 + v * 3.7 - motionTime * 0.36 + phase) * 0.12;

          if (random[i4 + 1] > 0.72) {
            currentX += Math.sin(motionTime * 0.38 + phase) * 0.055 * random[i4];
            currentY += (random[i4] - 0.5) * 0.32 + Math.sin(motionTime * 0.72 - u * 3.2 + phase) * 0.12 * random[i4];
            currentZ += Math.cos(motionTime * 0.47 + u * 4.4 + phase) * 0.18 * random[i4];
          }

          const vortex = Math.exp(-Math.pow(u - Math.sin(motionTime * 0.2) * 0.25, 2) * 3.4);
          const vortexAngle = vortex * Math.sin(motionTime * 0.68 + u * 2.1) * 0.72;
          const vortexY = currentY * Math.cos(vortexAngle) - currentZ * Math.sin(vortexAngle);
          currentZ = currentY * Math.sin(vortexAngle) + currentZ * Math.cos(vortexAngle);
          currentY = vortexY;

          const groupTurn = Math.sin(motionTime * 0.19) * 0.075 + Math.sin(motionTime * 0.37 + groupPhase) * 0.032;
          const turnedX = currentX * Math.cos(groupTurn) - currentY * Math.sin(groupTurn);
          const turnedY = currentX * Math.sin(groupTurn) + currentY * Math.cos(groupTurn);
          currentX = turnedX;
          currentY = turnedY;

          const perspective = 1 / (1.95 - currentZ * 0.36);
          let idleX = currentCenterX + currentX * flowScaleX * perspective;
          let idleY = currentCenterY - currentY * flowScaleY * perspective;
          // Give every dot its own speed and phase across both axes. A linked
          // diagonal component prevents the added motion reading as jitter.
          const individualSpeed = 0.28 + random[i4] * 0.68;
          const individualPhase = phase * 1.73 + random[i4 + 1] * Math.PI * 2;
          const independentX =
            Math.sin(elapsed * individualSpeed + individualPhase) * width * (0.02 + random[i4 + 1] * 0.045);
          const independentY =
            Math.cos(elapsed * (individualSpeed * 0.73) + individualPhase * 1.21) *
            height *
            (0.016 + random[i4] * 0.035);
          const diagonalDrift =
            Math.sin(elapsed * (0.18 + random[i4 + 1] * 0.21) + individualPhase * 0.67) *
            minimumViewportDimension *
            0.032;
          // A smaller roaming subset softens the cluster edges. The
          // continuous weight avoids creating a visibly separate particle tier.
          const roamingFloor = 0.58;
          const roamingStrength = Math.max(0, (random[i4] - roamingFloor) / (1 - roamingFloor));
          const roamingX =
            Math.sin(elapsed * (0.18 + random[i4 + 1] * 0.24) + individualPhase * 0.81) * width * 0.3 * roamingStrength;
          const roamingY =
            Math.cos(elapsed * (0.16 + random[i4] * 0.22) + individualPhase * 1.09) * height * 0.25 * roamingStrength;
          idleX += independentX + diagonalDrift + roamingX;
          idleY += independentY + diagonalDrift * (random[i4] > 0.5 ? 0.72 : -0.72) + roamingY;
          // Give the top-left quadrant its own roaming population. The weighted
          // subset blends into the main field rather than becoming another group.
          const topLeftStrength = Math.max(0, (random[i4 + 1] - 0.7) / 0.3);
          const topLeftX = -width * 0.3 + Math.sin(elapsed * (0.2 + random[i4] * 0.28) + phase * 1.37) * width * 0.28;
          const topLeftY =
            height * 0.27 + Math.cos(elapsed * (0.18 + random[i4 + 1] * 0.25) + phase * 1.71) * height * 0.24;
          idleX += topLeftX * topLeftStrength;
          idleY += topLeftY * topLeftStrength;
          const isEscapeParticle = random[i4] > 0.86;
          const escapeX = Math.cos(phase) * width * 0.78;
          const escapeY = Math.sin(phase) * height * 0.78;
          // Keep the opening response compact and wake-shaped rather than
          // cutting a large circular hole through the moving field.
          const flowHoverX = x - pointer.x + Math.sin(phase * 2.3) * flowHoverWidth * 0.52;
          const flowHoverY = y - pointer.y + Math.cos(phase * 1.7) * flowHoverHeight * 0.48;
          const flowHoverDistance = Math.hypot(flowHoverX / flowHoverWidth, flowHoverY / flowHoverHeight);

          // Use the original irregular flow interaction both before formation
          // and for particles already knocked out of the completed logo.
          const detachedX = x - logoX;
          const detachedY = y - logoY;
          const visiblyDetached = release[i] > 0.002 || detachedX * detachedX + detachedY * detachedY > 100;
          const interactionLockStart = lockTiming[i2];
          const interactionLockEnd = lockTiming[i2 + 1];
          const interactionLockProgress = formationStarted
            ? Math.max(0, Math.min(1, (formation - interactionLockStart) / (interactionLockEnd - interactionLockStart)))
            : 0;
          // During formation, keep the repulsion visible through most of each
          // particle's journey, then taper it away before final letter lock-in.
          const canUseFlowInteraction = formationLocked
            ? visiblyDetached
            : interactionIsActive && interactionLockProgress < 0.75;
          if (canUseFlowInteraction && pointerIsInteractive && flowHoverDistance < 1) {
            const hoverEdge = 1 - flowHoverDistance;
            const hoverStrength = hoverEdge * hoverEdge * (3 - 2 * hoverEdge);
            const isEnteringCurrent = flowHover[i] < 0.12;
            const formationInteractionScale =
              formationStarted && !formationLocked ? Math.max(0, 1 - interactionLockProgress) * 0.75 : 1;
            const flowResponse = 0.16 + hoverStrength * 0.22;
            flowHover[i] += (hoverStrength * formationInteractionScale - flowHover[i]) * flowResponse * dt;
            const repelDx = x - pointer.x;
            const repelDy = y - pointer.y;
            const repelDistance = Math.hypot(repelDx, repelDy) || 1;
            const repelScale = formationLocked ? 1 : formationInteractionScale;
            const repelForce = hoverStrength * pointer.energy * (3.5 + cursorSpeed * 0.28) * repelScale;

            velocity[i2] += (repelDx / repelDistance) * repelForce * dt;
            velocity[i2 + 1] += (repelDy / repelDistance) * repelForce * dt;

            if (isEnteringCurrent && hoverStrength > 0.2) {
              if (!formationLocked) {
                // Store a temporary outward destination so forming particles
                // visibly travel away and then ease back into their letter.
                const formationDisruptionDistance = (75 + random[i4 + 1] * 110) * formationInteractionScale;
                disruptionOffset[i2] = (repelDx / repelDistance) * formationDisruptionDistance;
                disruptionOffset[i2 + 1] = (repelDy / repelDistance) * formationDisruptionDistance;
              }
              const repelImpulse = (9 + hoverStrength * 10) * pointer.energy * repelScale;
              velocity[i2] += (repelDx / repelDistance) * repelImpulse;
              velocity[i2 + 1] += (repelDy / repelDistance) * repelImpulse;
            }
          } else if (cursorIsStill) {
            flowHover[i] *= restingHoverDamping;
          } else {
            flowHover[i] *= movingHoverDamping;
          }

          // Let particles continue locking into the logo during formation.
          // Full logo disruption becomes available only after it has resolved.
          if (formationLocked && pointerIsInteractive && releaseDistance < 1) {
            const releaseEdge = 1 - releaseDistance;
            const releaseStrength = releaseEdge * releaseEdge * (3 - 2 * releaseEdge);
            const wasAttached = release[i] < 0.12;
            const releaseTarget = releaseStrength * (isEscapeParticle ? 0.96 : 0.68);
            release[i] += (releaseTarget - release[i]) * (0.1 + releaseStrength * 0.14) * dt;

            if (wasAttached && releaseStrength > 0.2) {
              const disruptionAngle = phase * 2.37 + random[i4 + 1] * Math.PI * 3.4;
              const disruptionDistance = 85 + random[i4 + 1] * 155;
              disruptionOffset[i2] = Math.cos(disruptionAngle) * disruptionDistance;
              disruptionOffset[i2 + 1] = Math.sin(disruptionAngle) * disruptionDistance;
              const releaseDestinationX = isEscapeParticle ? escapeX : logoX + disruptionOffset[i2];
              const releaseDestinationY = isEscapeParticle ? escapeY : logoY + disruptionOffset[i2 + 1];
              const flowDx = releaseDestinationX - x;
              const flowDy = releaseDestinationY - y;
              const flowDistance = Math.hypot(flowDx, flowDy) || 1;
              const releaseImpulse = isEscapeParticle
                ? 3.4 + random[i4] * 4.2
                : 2.2 + random[i4] * 3.8 + releaseStrength * 1.2;
              velocity[i2] += (flowDx / flowDistance) * releaseImpulse;
              velocity[i2 + 1] += (flowDy / flowDistance) * releaseImpulse;
            }
          } else if (cursorIsStill) {
            const returnRate = 0.0058 + random[i4 + 1] * 0.0023;
            release[i] = Math.max(0, release[i] - returnRate * dt * returnBlend);
          }

          const lockStart = lockTiming[i2];
          const lockEnd = lockTiming[i2 + 1];
          const rawLockProgress = Math.max(0, Math.min(1, (formation - lockStart) / (lockEnd - lockStart)));
          const easedLockProgress =
            rawLockProgress * rawLockProgress * rawLockProgress * (rawLockProgress * (rawLockProgress * 6 - 15) + 10);
          const particleFormation = easedLockProgress * (1 - release[i]);
          const particleDrift = 1 - particleFormation;
          // The opening field keeps its flowing target offset. Released logo
          // particles respond through the outward velocity force above instead.
          const flowHoverOffset = formationLocked ? 0 : flowHover[i] * particleDrift;
          const formationReturnCurve = flowHoverOffset * (1 - flowHoverOffset) * 0.28;
          const hoverOffsetX =
            (pointerSweepX * 2.4 + Math.cos(phase * 1.7) * 155) * flowHoverOffset +
            disruptionOffset[i2] * flowHoverOffset -
            disruptionOffset[i2 + 1] * formationReturnCurve;
          const hoverOffsetY =
            (pointerSweepY * 2.4 + Math.sin(phase * 1.3) * 125) * flowHoverOffset +
            disruptionOffset[i2 + 1] * flowHoverOffset +
            disruptionOffset[i2] * formationReturnCurve;
          const disruptionRotation = elapsed * (0.1 + random[i4 + 1] * 0.16) * (random[i4] > 0.5 ? 1 : -1);
          const rotatedDisruptionX =
            disruptionOffset[i2] * Math.cos(disruptionRotation) -
            disruptionOffset[i2 + 1] * Math.sin(disruptionRotation);
          const rotatedDisruptionY =
            disruptionOffset[i2] * Math.sin(disruptionRotation) +
            disruptionOffset[i2 + 1] * Math.cos(disruptionRotation);
          const disruptionFlutter = Math.sin(elapsed * (0.65 + random[i4] * 0.8) + phase) * 18 * release[i];
          const disruptionBaseX = isEscapeParticle ? escapeX - logoX : rotatedDisruptionX;
          const disruptionBaseY = isEscapeParticle ? escapeY - logoY : rotatedDisruptionY;
          const returnDirection = random[i4 + 1] > 0.5 ? 1 : -1;
          const returnCurve = release[i] * (1 - release[i]) * (0.3 + random[i4] * 0.35) * returnDirection * returnBlend;
          const returnOffsetX = -disruptionBaseY * returnCurve;
          const returnOffsetY = disruptionBaseX * returnCurve;
          const releasedFlowMix = isEscapeParticle ? 0 : release[i] * (0.58 + random[i4 + 1] * 0.32);
          const releasedFlowX = (idleX - logoX) * releasedFlowMix;
          const releasedFlowY = (idleY - logoY) * releasedFlowMix;
          const disruptionX =
            disruptionBaseX * release[i] + releasedFlowX + returnOffsetX + Math.cos(phase) * disruptionFlutter;
          const disruptionY =
            disruptionBaseY * release[i] + releasedFlowY + returnOffsetY + Math.sin(phase * 1.3) * disruptionFlutter;
          const routeProgress = Math.sin(easedLockProgress * Math.PI) * (1 - release[i]);
          const routeDirection = random[i4 + 1] > 0.5 ? 1 : -1;
          const routeCurve = (0.06 + random[i4] * 0.08) * routeDirection * routeProgress;
          const routeOffsetX = (logoY - idleY) * routeCurve;
          const routeOffsetY = (idleX - logoX) * routeCurve;
          const targetX =
            idleX + (logoX - idleX) * easedLockProgress + routeOffsetX + disruptionX * easedLockProgress + hoverOffsetX;
          const targetY =
            idleY + (logoY - idleY) * easedLockProgress + routeOffsetY + disruptionY * easedLockProgress + hoverOffsetY;

          // Keep released dots under soft target and velocity forces so their
          // disruption and return remain continuous rather than stop-start.
          const gatheringBoost = formation * (1 - particleFormation) * 0.001;
          // Keep the early resolve soft, then progressively strengthen the
          // spring near lock-in so particles settle quickly without snapping.
          const settlingBoost = easedLockProgress * easedLockProgress * 0.007;
          const formationPull = 0.0045 + settlingBoost;
          const releasedPullSoftening = formationLocked ? 1 - release[i] * 0.58 : 1;
          const targetPull = (0.004 + particleFormation * formationPull + gatheringBoost) * releasedPullSoftening;
          velocity[i2] += (targetX - x) * targetPull * dt;
          velocity[i2 + 1] += (targetY - y) * targetPull * dt;
          velocity[i2] += Math.cos(y * 0.008 + elapsed * 0.72 + phase) * (0.012 + particleDrift * 0.203) * dt;
          velocity[i2 + 1] += Math.sin(x * 0.007 - elapsed * 0.61 + phase) * (0.01 + particleDrift * 0.17) * dt;
          const releasedTurbulence = isEscapeParticle ? 0 : release[i] * (0.12 + random[i4] * 0.16);
          velocity[i2] += Math.cos(elapsed * (0.82 + random[i4 + 1] * 0.9) + phase * 2.1) * releasedTurbulence * dt;
          velocity[i2 + 1] +=
            Math.sin(elapsed * (0.68 + random[i4] * 0.84) + phase * 1.63) * releasedTurbulence * 0.82 * dt;
          const openingHoverTurbulence = formationLocked ? 0 : flowHoverOffset * (0.75 + random[i4] * 0.65);
          velocity[i2] += Math.cos(elapsed * (0.9 + random[i4 + 1] * 1.1) + phase * 2.4) * openingHoverTurbulence * dt;
          velocity[i2 + 1] +=
            Math.sin(elapsed * (0.74 + random[i4] * 0.96) + phase * 1.8) * openingHoverTurbulence * 0.86 * dt;

          const speed = Math.hypot(velocity[i2], velocity[i2 + 1]);
          const maxSpeed =
            4 + particleDrift * 6 + scatterEnergy * 7 + formation * (1 - particleFormation) * 3.5 + flowHoverOffset * 4;
          if (speed > maxSpeed) {
            velocity[i2] = (velocity[i2] / speed) * maxSpeed;
            velocity[i2 + 1] = (velocity[i2 + 1] / speed) * maxSpeed;
          }

          const settlingDamping = 0.01 + easedLockProgress * 0.008;
          const damping = Math.pow(0.948 - particleFormation * settlingDamping + release[i] * 0.03, dt);
          velocity[i2] *= damping;
          velocity[i2 + 1] *= damping;
          position[i3] += velocity[i2] * dt;
          position[i3 + 1] += velocity[i2 + 1] * dt;

          // Close the remaining distance with a progressively ramped positional
          // blend. It is strongest only at lock-in and fades out for disrupted
          // particles, preserving their free hover movement.
          const settleAvailability = Math.max(0, 1 - release[i] * 1.4);
          const precisionSettle =
            easedLockProgress * easedLockProgress * easedLockProgress * 0.065 * settleAvailability;
          position[i3] += (targetX - position[i3]) * precisionSettle * dt;
          position[i3 + 1] += (targetY - position[i3 + 1]) * precisionSettle * dt;

          const targetDepth = currentZ * 7 * particleDrift;
          position[i3 + 2] += (targetDepth - position[i3 + 2]) * 0.055 * dt;
        }

        geometry.attributes.position.needsUpdate = true;
        geometry.attributes.aRelease.needsUpdate = true;
        renderer.render(scene, camera);
      }

      resize();
      window.addEventListener("resize", resize, { passive: true });
      surface.addEventListener("pointermove", updatePointer, { passive: true });
      surface.addEventListener(
        "pointerdown",
        event => {
          updatePointer(event);
          if (!formationLocked) {
            registerInteraction();
          }
        },
        { passive: true }
      );
      surface.addEventListener("pointerleave", clearPointer, { passive: true });
      document.querySelector('[data-action="scatter"]')?.addEventListener("click", () => {
        registerInteraction();
        pointer.x = 0;
        pointer.y = height * 0.18;
        pointer.energy = 1;
        burst(1);
      });
      document.querySelector('[data-action="replay"]')?.addEventListener("click", replay);
      window.addEventListener("keydown", registerInteraction, { passive: true });
      document.addEventListener("visibilitychange", () => {
        visible = !document.hidden;
        lastTime = performance.now();
      });

      animationFrame = requestAnimationFrame(animate);

      // Only prepare the alternate mark if the viewport actually crosses the
      // responsive logo breakpoint. Normal visits now decode and scan one SVG.
      const handleLogoLayoutChange = event => {
        const existingLogoPoints = event.matches ? compactLogoPoints : desktopLogoPoints;
        if (existingLogoPoints) {
          resize();
          return;
        }

        const alternateSource = event.matches ? compactLogoSource : desktopLogoSource;
        const alternateAspectRatio = event.matches ? 275 / 133 : 14.08;
        sampleLogo(alternateSource, count, alternateAspectRatio)
          .then(alternateLogoPoints => {
            if (event.matches) {
              compactLogoPoints = alternateLogoPoints;
            } else {
              desktopLogoPoints = alternateLogoPoints;
            }

            if (stackedLogoQuery.matches === event.matches) {
              resize();
            }
          })
          .catch(() => {});
      };
      stackedLogoQuery.addEventListener("change", handleLogoLayoutChange);

      let disposed = false;
      const disposeAnimation = () => {
        if (disposed) return;
        disposed = true;
        visible = false;
        cancelAnimationFrame(animationFrame);
        renderer.dispose();
        geometry.dispose();
        material.dispose();
        stackedLogoQuery.removeEventListener("change", handleLogoLayoutChange);
      };
      surface.closest(".splash")?.addEventListener("splash:dismissed", disposeAnimation, { once: true });

      reduceMotion.addEventListener(
        "change",
        event => {
          if (!event.matches) return;
          disposeAnimation();
        },
        { once: true }
      );
    });
  });
}

function sampleLogo(source, total, aspectRatio) {
  const image = new Image();
  image.src = source;
  return image.decode().then(() => {
    const sampleWidth = aspectRatio > 5 ? 1408 : 700;
    const sampleHeight = Math.max(100, Math.round(sampleWidth / aspectRatio));
    const buffer = document.createElement("canvas");
    buffer.width = sampleWidth;
    buffer.height = sampleHeight;
    const context = buffer.getContext("2d", { willReadFrequently: true });
    context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
    const pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
    const candidates = [];
    const isStackedSample = aspectRatio < 5;
    const sampleStep = 2;
    const alphaThreshold = isStackedSample ? 225 : 190;

    for (let y = 1; y < sampleHeight - 1; y += sampleStep) {
      for (let x = 1; x < sampleWidth - 1; x += sampleStep) {
        if (pixels[(y * sampleWidth + x) * 4 + 3] > alphaThreshold) {
          // Store one pixel index rather than an object for every candidate.
          // The stacked logo can contain hundreds of thousands of samples, so
          // this substantially reduces temporary allocations during startup.
          candidates.push(y * sampleWidth + x);
        }
      }
    }

    // Shuffle so all letterforms fill at the same rate instead of left-to-right.
    for (let i = candidates.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    const points = new Float32Array(total * 2);
    const horizontalJitter = isStackedSample ? 0.00015 : 0.0006;
    const verticalJitter = isStackedSample ? 0.0007 : 0.006;

    for (let index = 0; index < total; index += 1) {
      const pixelIndex = candidates[index % candidates.length];
      const pointIndex = index * 2;
      points[pointIndex] = (pixelIndex % sampleWidth) / sampleWidth + (Math.random() - 0.5) * horizontalJitter;
      points[pointIndex + 1] =
        Math.floor(pixelIndex / sampleWidth) / sampleHeight + (Math.random() - 0.5) * verticalJitter;
    }

    return points;
  });
}

/* jshint ignore:end */
